// String helpers: casing, truncation, slugs, value display, and org-name
// normalization.

export const capitalize = (value: unknown): string => {
  if (!value) return ""
  const s = String(value)
  return s.charAt(0).toUpperCase() + s.slice(1)
}

// capitalize every space-separated word: "deal flow" -> "Deal Flow"
export const titleCase = (value: unknown): string => {
  if (!value) return ""
  return String(value)
    .split(" ")
    .map((word) => capitalize(word))
    .join(" ")
}

// cut a string to <limit> characters and append <ellipsis> when it exceeds
// the limit (so truncated output is limit + ellipsis.length characters).
// trim removes trailing whitespace from the cut point before the ellipsis.
export const truncate = (
  string: string,
  limit: number,
  { ellipsis = "...", trim = false }: { ellipsis?: string; trim?: boolean } = {}
) => {
  if (string.length <= limit) return string
  const truncated = string.slice(0, limit)
  return (trim ? truncated.trimEnd() : truncated) + ellipsis
}

/**
 * Format a value for display in history entries.
 * Handles null, boolean, objects, and long strings with truncation.
 *
 * @param {unknown} value - The value to format
 * @param {number} maxLength - Maximum length before truncation (default: 100)
 * @returns {string} Formatted string representation
 */
export function formatValue(value: unknown, maxLength = 100): string {
  if (value === null || value === undefined) return "(empty)"
  if (typeof value === "boolean") return value ? "Yes" : "No"
  if (typeof value === "object") {
    const json = JSON.stringify(value)
    if (json.length > maxLength) {
      return json.substring(0, maxLength) + "…"
    }
    return json
  }
  const str = String(value)
  if (str.length > maxLength) {
    return str.substring(0, maxLength) + "…"
  }
  return str
}

export const slugify = (name: string | null | undefined): string => {
  return (name || "")
    .toString()
    .toLowerCase()
    .trim()
    .replace(/\s+/g, "-")
    .replace(/[^\w-]+/g, "")
    .replace(/--+/g, "-")
    .replace(/^-+/, "")
    .replace(/-+$/, "")
}

/** records.slug's column width. Core stores an explicit slug as given, so a
 *  slug built from parts has to fit this itself. */
export const SLUG_MAX_LENGTH = 64

/** `base-suffix`, with `base` cut so the whole fits SLUG_MAX_LENGTH. The suffix
 *  — the timestamp, date or random id that keeps the slug unique — stays whole. */
export function fitSlug(base: string, suffix: string): string {
  const head = base.slice(0, Math.max(SLUG_MAX_LENGTH - suffix.length - 1, 0)).replace(/-+$/, "")
  return head ? `${head}-${suffix}` : suffix.slice(0, SLUG_MAX_LENGTH)
}

// Inverse of slugify for display: "deals-pipeline" → "Deals Pipeline".
// Handles possessive ("foo-s-bar" → "Foo's Bar") and Roman numerals (i-x).
export function formatSlug(slug: string | null | undefined): string {
  if (!slug) return ""

  const words = slug.split("-")
  const formatted: string[] = []

  for (const word of words) {
    if (word === "s" && formatted.length > 0) {
      formatted[formatted.length - 1] += "'s"
      continue
    }
    if (/^(i|ii|iii|iv|v|vi|vii|viii|ix|x)$/i.test(word)) {
      formatted.push(word.toUpperCase())
      continue
    }
    formatted.push(word.charAt(0).toUpperCase() + word.slice(1))
  }

  return formatted.join(" ")
}

// Organization-name normalization used by duplicate detection.
//
// Two org names are treated as the same when their normalized forms are equal.
// Normalization lowercases, strips punctuation, drops common legal suffixes
// (inc, llc, ltd, …), and collapses whitespace. It deliberately does NOT strip
// product suffixes like "AI" — "Tonic" and "Tonic AI" can be different
// companies and must not collapse to the same key.
export function normalizeOrgName(name: string): string {
  return (name || "")
    .toLowerCase()
    .replace(/[.,'"’]/g, "")
    .replace(/\b(inc|llc|ltd|gmbh|plc|co|corp|corporation|company)\b/g, "")
    .replace(/\s+/g, " ")
    .trim()
}

export default {
  capitalize,
  titleCase,
  truncate,
  formatValue,
  slugify,
  fitSlug,
  formatSlug,
  normalizeOrgName,
}
